import React, { useState } from 'react';
import { lists } from '../data/lists';

// ── Single list editor panel ───────────────────────────────────────────────────
function ListPanel({ listKey, label, icon, description, onChanged }) {
  const [items, setItems] = useState(() => {
    const getter = `get${listKey.charAt(0).toUpperCase() + listKey.slice(1)}`;
    return lists[getter]?.() || [];
  });
  const [newVal, setNewVal] = useState('');
  const [editIdx, setEditIdx] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);

  const refresh = () => {
    const getter = `get${listKey.charAt(0).toUpperCase() + listKey.slice(1)}`;
    setItems(lists[getter]?.() || []);
    onChanged?.();
  };

  const handleAdd = () => {
    if (!newVal.trim()) return;
    const ok = lists.addTo(listKey, newVal);
    if (ok) { setNewVal(''); refresh(); }
    else alert(`"${newVal.trim()}" already exists in this list.`);
  };

  const handleRemove = (val) => {
    lists.removeFrom(listKey, val);
    refresh();
  };

  const startEdit = (idx, val) => {
    setEditIdx(idx); setEditVal(val);
  };

  const saveEdit = (oldVal) => {
    if (editVal.trim() && editVal !== oldVal) {
      lists.renameIn(listKey, oldVal, editVal);
      refresh();
    }
    setEditIdx(null);
  };

  const handleReset = () => {
    lists.resetList(listKey);
    setConfirmReset(false);
    refresh();
  };

  return (
    <div className="panel">
      <div className="panel-header">
        {icon} {label}
        <span style={{marginLeft:8,fontSize:10,fontWeight:400,color:'#aac4e0'}}>{description}</span>
        <span style={{marginLeft:'auto',fontSize:11,fontWeight:400}}>
          {items.length} item{items.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="panel-body" style={{padding:10}}>

        {/* Add new */}
        <div style={{display:'flex',gap:6,marginBottom:10}}>
          <input
            className="search-input"
            style={{flex:1}}
            placeholder={`Add new ${label.toLowerCase().replace(/s$/,'')}...`}
            value={newVal}
            onChange={e => setNewVal(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <button className="btn btn-primary" onClick={handleAdd}>＋ Add</button>
        </div>

        {/* Items list */}
        <div style={{maxHeight:220,overflowY:'auto',border:'1px solid #e0e4ec',borderRadius:4}}>
          {items.length === 0 && (
            <div className="text-muted text-sm" style={{padding:'12px 10px',textAlign:'center'}}>No items — add one above</div>
          )}
          {items.map((val, idx) => (
            <div key={val} style={{
              display:'flex',alignItems:'center',gap:6,
              padding:'5px 8px',borderBottom:'1px solid #f0f2f8',
              background: editIdx === idx ? '#f0f4ff' : idx % 2 === 0 ? 'white' : '#fafbff',
            }}>
              {editIdx === idx ? (
                <>
                  <input
                    autoFocus
                    value={editVal}
                    onChange={e => setEditVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(val); if (e.key === 'Escape') setEditIdx(null); }}
                    style={{flex:1,border:'1px solid #1e3060',borderRadius:3,padding:'3px 6px',fontSize:12,outline:'none'}}
                  />
                  <button className="btn btn-sm btn-primary" onClick={() => saveEdit(val)}>✓ Save</button>
                  <button className="btn btn-sm" onClick={() => setEditIdx(null)}>✕</button>
                </>
              ) : (
                <>
                  <span style={{flex:1,fontSize:12}}>{val}</span>
                  <button className="btn btn-sm" onClick={() => startEdit(idx, val)} title="Rename">✏️</button>
                  <button className="btn btn-sm" style={{color:'#b83232'}} onClick={() => handleRemove(val)} title="Remove">🗑</button>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Reset */}
        <div style={{marginTop:8,display:'flex',justifyContent:'flex-end'}}>
          {confirmReset ? (
            <div style={{display:'flex',alignItems:'center',gap:6,fontSize:11}}>
              <span style={{color:'#b83232'}}>Reset to defaults?</span>
              <button className="btn btn-sm btn-danger" onClick={handleReset}>Yes, reset</button>
              <button className="btn btn-sm" onClick={() => setConfirmReset(false)}>Cancel</button>
            </div>
          ) : (
            <button className="btn btn-sm" style={{color:'#888'}} onClick={() => setConfirmReset(true)}>
              ↺ Reset to defaults
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main ListEditor modal ─────────────────────────────────────────────────────
export default function ListEditor({ onClose, onChanged }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{width:820,maxHeight:'90vh'}} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚙️ Edit Dropdown Lists</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{overflowY:'auto'}}>
          <div className="notice notice-info" style={{marginBottom:14}}>
            Changes are saved immediately and appear in all dropdown menus throughout the app.
            Click ✏️ to rename, 🗑 to remove, or type and press Enter / ＋ Add to add new items.
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            {lists.ALL_LISTS.map(l => (
              <ListPanel key={l.key} listKey={l.key} label={l.label} icon={l.icon} description={l.description} onChanged={onChanged} />
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>✓ Done</button>
        </div>
      </div>
    </div>
  );
}
